import React, { FC, useEffect } from 'react';
import { Container } from '@montana/shared/container';
import { ProgressBar, IProgressStep } from '../../checkout/progress-bar';
import { useTranslations } from '@montana/containers/translations';
import { ICheckoutPage, IM18USPPromoBar, IShippingPage } from '@montana/models/generated/cms.interface';
import { Usp } from '@montana/shared/usp';
import { Button, ButtonStyles } from '@montana/shared/button';
import { Translate } from '@montana/shared/translate';
import { Link } from '@montana/shared/link';
import { ItemSummary } from '../item-summary';
import { useCart } from '@montana/containers/cart';
import { StaticHeroComponent } from '@montana/shared/static-hero/static-hero.component';
import { usePage } from '@montana/containers/page';
import { useDynamicSettings } from '@montana/containers/dynamic-settings';
import {GuaranteeLink} from "@montana/shared/guarantee-link";

export enum CheckoutSteps {
    PersonalInformation,
    Delivery,
    Payment
}

interface ICheckoutB2CBaseProps extends ICheckoutPage, IShippingPage {
    step: CheckoutSteps;
    showStepper?: boolean;
    ctaText: string;
    children: JSX.Element;
    onCTAClick?: () => void;
}

export const CheckoutB2CBase: FC<ICheckoutB2CBaseProps> = (props: ICheckoutB2CBaseProps) => {
    const { bottomBlocks, children, ctaText, onCTAClick, showStepper = false } = props;
    const { setPageHasHero } = usePage();
    const { lines, totalPrice, totalVatAmount, dispatchTime, deliveryPrice } = useCart();
    const translate = useTranslations();
    const { links } = useDynamicSettings();

    useEffect(() => {
        if (showStepper) {
            setPageHasHero(true);
        }
    }, []);

    const steps: IProgressStep[] = [
        {
            title: translate(t => t.checkoutPage.progressBarB2cCustomerInformation, '[CUSTOMER INFORMATION]'),
            link: links?.checkoutB2C ?? '/'
        },
        {
            title: translate(t => t.checkoutPage.progressBarB2cShippingInformation, '[SHIPPING INFORMATION]'),
            link: links?.checkoutShippingPage ?? '/'
        },
        {
            title: translate(t => t.checkoutPage.progressBarB2cPaymentMethod, '[PAYMENT METHOD]'),
            link: '',
        }, {
            title: translate(t => t.checkoutPage.progressBarB2cReceipt, '[RECEIPT]'),
            link: '',
        }
    ];

    const uspBlock: IM18USPPromoBar | null = bottomBlocks && bottomBlocks.length
        ? bottomBlocks[0]
        : null;

    return (
        <Container className="checkout">
            {showStepper && (
                <StaticHeroComponent className="checkout__hero">
                    <div className="checkout__hero-content">
                        <ProgressBar
                            currentIndex={props.step}
                            steps={steps}
                        />
                    </div>
                </StaticHeroComponent>
            )}
            <div className="checkout-b2c-base">
                <div className="checkout-b2c-base__col">
                    {children}
                </div>
                <div className="checkout-b2c-base__col checkout-b2c-base-col2">
                    <div className="checkout-b2c-base-col2__content">
                        <div className="checkout-b2c-base-bag">
                            <div className="checkout-b2c-base-bag__header">
                                <span className="checkout-b2c-base-bag__your-bag">
                                    <Translate value="checkoutPage.yourBag" defaultValue="[Your bag]" />
                                </span>
                                <Link to={links?.cart ?? '/'}>
                                    <Translate value="checkoutPage.editBag" defaultValue="[Edit shopping bag]" />
                                </Link>
                            </div>
                            <ItemSummary
                                lines={lines}
                                totalPrice={totalPrice}
                                totalVatAmount={totalVatAmount}
                                dispatchTime={dispatchTime}
                                deliveryPrice={deliveryPrice}
                                showShippingCost={props.step !== CheckoutSteps.PersonalInformation}
                            />
                            <Button
                                className="checkout-b2c-base__cta"
                                style={ButtonStyles.Primary}
                                text={ctaText}
                                onClick={onCTAClick}
                            />
                            <GuaranteeLink />
                        </div>
                        {uspBlock &&
                            <div className="cart-checkout-usp">
                                {uspBlock.usPs?.map((usp, index) => (
                                    <Usp key={`usp-block__${uspBlock.epiId}__${index}`} usp={usp} />
                                ))}
                            </div>
                        }
                    </div>
                </div>
            </div>
        </Container>
    )
}
